import axios, { AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { getRawInitData } from '@/env';
import { syncServerTime } from '@/lib/serverClock';

const baseURL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export const api: AxiosInstance = axios.create({
    baseURL: `${baseURL}/api`,
    timeout: 30000,
    headers: { Accept: 'application/json' },
});

const TOKEN_KEY = 'bingo.miniapp.token';
const USER_KEY = 'bingo.miniapp.user';

// Routes that authenticate via Telegram initData rather than a bearer token.
// Everything else uses the cached Sanctum token (and bootstraps one on demand
// via lib/auth.ts).
const INITDATA_ROUTES = ['/auth/telegram'];

function isInitDataRoute(url: string | undefined): boolean {
    if (!url) return false;
    return INITDATA_ROUTES.some((route) => url === route || url.endsWith(route));
}

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    // Allow callers to opt out / override (e.g. ensureAuthToken sets the
    // Authorization header explicitly before calling /auth/telegram).
    if (config.headers.get('Authorization')) {
        return config;
    }

    if (isInitDataRoute(config.url)) {
        const initData = getRawInitData();
        if (initData) {
            config.headers.set('Authorization', `tma ${initData}`);
        }
        return config;
    }

    // Lazy import to avoid an import cycle (auth.ts imports api).
    const { ensureAuthToken } = await import('@/lib/auth');
    try {
        const token = await ensureAuthToken();
        config.headers.set('Authorization', `Bearer ${token}`);
    } catch {
        // No initData and no cached token — let the request go out unauthenticated;
        // the backend will respond 401 and the UI will surface it.
    }
    return config;
});

function applyServerTimeFromResponse(response: AxiosResponse | undefined): void {
    const headers = response?.headers;
    const header =
        headers?.['x-server-time'] ??
        headers?.['X-Server-Time'] ??
        (typeof headers?.get === 'function' ? headers.get('x-server-time') : null);
    if (header !== undefined && header !== null && header !== '') {
        syncServerTime(Array.isArray(header) ? header[0] : String(header));
        return;
    }
    const body = response?.data as
        | { server_time?: number; data?: { server_time?: number } | Array<{ server_time?: number }> }
        | undefined;
    if (body?.server_time !== undefined) {
        syncServerTime(body.server_time);
        return;
    }
    const data = body?.data;
    if (data && !Array.isArray(data) && data.server_time !== undefined) {
        syncServerTime(data.server_time);
        return;
    }
    if (Array.isArray(data) && data[0]?.server_time !== undefined) {
        syncServerTime(data[0].server_time);
    }
}

// Sync server clock on every response; 401 → clear cached token.
api.interceptors.response.use(
    (r) => {
        applyServerTimeFromResponse(r);
        return r;
    },
    (error: AxiosError) => {
        applyServerTimeFromResponse(error.response);
        if (error.response?.status === 401) {
            try {
                sessionStorage.removeItem(TOKEN_KEY);
                sessionStorage.removeItem(USER_KEY);
            } catch {
                // ignore
            }
        }
        return Promise.reject(error);
    },
);
